import {
  Alert,
  Badge,
  Button,
  CopyButton,
  Divider,
  Group,
  Modal,
  rem,
  Stack,
  Table,
  Text,
  ActionIcon,
  Tooltip,
  SegmentedControl,
  Title,
  ThemeIcon,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import {
  IconAlertTriangle,
  IconCheck,
  IconCircleCheck,
  IconCopy,
  IconInfoCircle,
  IconRefresh,
  IconX,
} from "@tabler/icons-react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";

// ─── Demo data ────────────────────────────────────────────────────────────────

type SubStatus = "ACTIVE" | "SUSPENDED" | "CANCELLED";
type PaymentStatus = "COMPLETED" | "FAILED" | "REFUNDED";

interface DemoKey {
  key: string;
  active: boolean;
}

interface DemoPayment {
  date: string;
  amount: number;
  status: PaymentStatus;
  description: string;
}

const DEMO_SUB = {
  paypalSubId: "I-BW452GLLEP1G",
  plan: "All Apps Bundle",
  pricePerUnit: 19.99,
  quantity: 2,
  billingCycle: "3 months",
  startDate: "15 Jan 2026",
  nextBillingDate: "15 Apr 2026",
  accessUntil: "15 Apr 2026",
  keys: [
    { key: "SRSUB-A3F2-9KL1-MXQP", active: true },
    { key: "SRSUB-B7T4-2NW8-JYRV", active: true },
  ] as DemoKey[],
};

const DEMO_PAYMENTS: DemoPayment[] = [
  { date: "15 Jan 2026", amount: 39.98, status: "COMPLETED", description: "Subscription renewal" },
  { date: "15 Oct 2025", amount: 39.98, status: "COMPLETED", description: "Subscription renewal" },
  { date: "20 Jul 2025", amount: 39.98, status: "FAILED",    description: "Retry — card declined" },
  { date: "15 Jul 2025", amount: 39.98, status: "FAILED",    description: "Automatic payment" },
  { date: "15 Apr 2025", amount: 39.98, status: "COMPLETED", description: "Subscription renewal" },
  { date: "15 Jan 2025", amount: 39.98, status: "COMPLETED", description: "Initial payment" },
];

// ─── Helpers ──────────────────────────────────────────────────────────────────

const STATUS_CONFIG: Record<SubStatus, { color: string; label: string }> = {
  ACTIVE:    { color: "green",  label: "Active" },
  SUSPENDED: { color: "yellow", label: "Suspended" },
  CANCELLED: { color: "red",    label: "Cancelled" },
};

const PAYMENT_CONFIG: Record<PaymentStatus, { color: string; icon: React.ReactNode }> = {
  COMPLETED: { color: "green", icon: <IconCircleCheck size={14} /> },
  FAILED:    { color: "red",   icon: <IconX size={14} /> },
  REFUNDED:  { color: "gray",  icon: <IconInfoCircle size={14} /> },
};

// ─── Component ────────────────────────────────────────────────────────────────

export function SubscriptionPanel() {
  const [status, setStatus] = useState<SubStatus>("ACTIVE");
  const [reactivating, setReactivating] = useState(false);
  const [cancelOpened,     { open: openCancel,     close: closeCancel }]     = useDisclosure(false);
  const [reactivateOpened, { open: openReactivate, close: closeReactivate }] = useDisclosure(false);
  const navigate = useNavigate();

  const handleConfirmCancel = () => {
    setStatus("CANCELLED");
    closeCancel();
  };

  // SUSPENDED: calls POST /dyn/manage-subscription.php?action=activate in production.
  // PayPal endpoint: POST /v1/billing/subscriptions/{id}/activate
  const handleConfirmReactivate = async () => {
    setReactivating(true);
    // Demo: simulate a short network call then flip to ACTIVE
    await new Promise((r) => setTimeout(r, 1200));
    setStatus("ACTIVE");
    setReactivating(false);
    closeReactivate();
  };

  const total = (DEMO_SUB.pricePerUnit * DEMO_SUB.quantity).toFixed(2);
  const keys: DemoKey[] = DEMO_SUB.keys.map((k) => ({
    ...k,
    active: status === "ACTIVE",
  }));

  return (
    <>
      {/* ── Cancel modal ── */}
      <Modal opened={cancelOpened} onClose={closeCancel} title="Cancel Subscription" centered>
        <Text size="sm" mb="md">
          Are you sure you want to cancel your <strong>{DEMO_SUB.plan}</strong> subscription?
          You will keep access to all apps until <strong>{DEMO_SUB.accessUntil}</strong>.
        </Text>
        <Group justify="flex-end" gap="sm">
          <Button variant="default" onClick={closeCancel}>Keep Subscription</Button>
          <Button color="red" onClick={handleConfirmCancel}>Yes, Cancel</Button>
        </Group>
      </Modal>

      {/* ── Reactivate modal (SUSPENDED only) ── */}
      <Modal opened={reactivateOpened} onClose={closeReactivate} title="Reactivate Subscription" centered>
        <Text size="sm" mb="xs">
          This will ask PayPal to retry the last failed payment using your payment method on file.
        </Text>
        <Text size="sm" c="dimmed" mb="md">
          If the payment succeeds, your subscription and license keys will be reactivated immediately.
          If it fails again, you may need to update your payment method in your PayPal account first.
        </Text>
        <Group justify="flex-end" gap="sm">
          <Button variant="default" onClick={closeReactivate} disabled={reactivating}>Cancel</Button>
          <Button
            color="green"
            leftSection={<IconRefresh size={14} />}
            loading={reactivating}
            onClick={handleConfirmReactivate}
          >
            Retry Payment
          </Button>
        </Group>
      </Modal>

      <Stack gap="md">

        {/* ── Demo state switcher ── */}
        <Alert icon={<IconInfoCircle size={16} />} color="blue" variant="light" title="Demo mode">
          <Text size="xs" mb="xs">Simulate different subscription states:</Text>
          <SegmentedControl
            size="xs"
            value={status}
            onChange={(v) => setStatus(v as SubStatus)}
            data={[
              { label: "Active",    value: "ACTIVE" },
              { label: "Suspended", value: "SUSPENDED" },
              { label: "Cancelled", value: "CANCELLED" },
            ]}
          />
        </Alert>

        {/* ── Status banners + reactivation CTAs ── */}
        {status === "SUSPENDED" && (
          <Alert icon={<IconAlertTriangle size={16} />} color="yellow" title="Payment failed">
            <Text size="sm" mb="sm">
              Your last payment could not be processed. Update your payment method in PayPal,
              then click <strong>Retry Payment</strong> to restore access.
              Keys remain active until <strong>{DEMO_SUB.nextBillingDate}</strong>.
            </Text>
            <Button
              size="xs"
              color="yellow"
              variant="filled"
              leftSection={<IconRefresh size={14} />}
              onClick={openReactivate}
            >
              Retry Payment
            </Button>
          </Alert>
        )}

        {status === "CANCELLED" && (
          <Alert icon={<IconInfoCircle size={16} />} color="gray" title="Subscription cancelled">
            <Text size="sm" mb="sm">
              Your subscription has been cancelled. You retain access until{" "}
              <strong>{DEMO_SUB.accessUntil}</strong>. Cancelled subscriptions cannot be
              reactivated — a new subscription is required.
            </Text>
            <Button
              size="xs"
              color="green"
              variant="filled"
              onClick={() => navigate("/subscribe")}
            >
              Start New Subscription
            </Button>
          </Alert>
        )}

        {/* ── Subscription overview ── */}
        <div style={{ border: "1px solid #2a2a2a", borderRadius: 8, padding: "1rem" }}>
          <Group justify="space-between" mb="sm" wrap="nowrap">
            <div>
              <Title order={4}>{DEMO_SUB.plan}</Title>
              <Text size="sm" c="dimmed">
                {DEMO_SUB.quantity} × 19.99€ = <strong>{total}€</strong> every {DEMO_SUB.billingCycle}
              </Text>
            </div>
            <Badge key={status} color={STATUS_CONFIG[status].color} variant="filled" size="lg">
              {STATUS_CONFIG[status].label}
            </Badge>
          </Group>

          <Divider my="sm" />

          <div style={{ display: "grid", gridTemplateColumns: "160px 1fr", gap: "6px 12px" }}>
            <Text size="sm" c="dimmed">Subscription ID</Text>
            <Text size="sm">{DEMO_SUB.paypalSubId}</Text>
            <Text size="sm" c="dimmed">Started</Text>
            <Text size="sm">{DEMO_SUB.startDate}</Text>
            {status === "ACTIVE" ? (
              <>
                <Text size="sm" c="dimmed">Next billing</Text>
                <Text size="sm">{DEMO_SUB.nextBillingDate}</Text>
              </>
            ) : (
              <>
                <Text size="sm" c="dimmed">Access until</Text>
                <Text size="sm">{DEMO_SUB.accessUntil}</Text>
              </>
            )}
          </div>

          {status === "ACTIVE" && (
            <Group justify="flex-end" mt="md">
              <Button variant="subtle" color="red" size="xs" onClick={openCancel}>
                Cancel subscription
              </Button>
            </Group>
          )}
        </div>

        {/* ── License keys ── */}
        <div style={{ border: "1px solid #2a2a2a", borderRadius: 8, padding: "1rem" }}>
          <Text fw={600} mb="sm">License Keys</Text>
          <Stack gap="xs">
            {keys.map((k) => (
              <Group key={k.key} justify="space-between" gap="sm" wrap="nowrap">
                <Group gap="xs" style={{ minWidth: 0 }}>
                  <ThemeIcon size="sm" radius="xl" color={k.active ? "green" : "gray"} variant="light" style={{ flexShrink: 0 }}>
                    {k.active ? <IconCheck size={12} /> : <IconX size={12} />}
                  </ThemeIcon>
                  <Text size="sm" ff="monospace" style={{ wordBreak: "break-all" }}>{k.key}</Text>
                </Group>
                <Group gap="xs" style={{ flexShrink: 0 }}>
                  <Badge size="xs" color={k.active ? "green" : "gray"} variant="light">
                    {k.active ? "Active" : "Inactive"}
                  </Badge>
                  <CopyButton value={k.key} timeout={2000}>
                    {({ copied, copy }) => (
                      <Tooltip label={copied ? "Copied!" : "Copy key"} withArrow position="right">
                        <ActionIcon size="sm" color={copied ? "teal" : "gray"} variant="subtle" onClick={copy}>
                          {copied
                            ? <IconCheck style={{ width: rem(14) }} />
                            : <IconCopy style={{ width: rem(14) }} />}
                        </ActionIcon>
                      </Tooltip>
                    )}
                  </CopyButton>
                </Group>
              </Group>
            ))}
          </Stack>
        </div>

        {/* ── Payment history ── */}
        <div style={{ border: "1px solid #2a2a2a", borderRadius: 8, padding: "1rem" }}>
          <Text fw={600} mb="sm">Payment History</Text>
          <Table.ScrollContainer minWidth={420}>
            <Table striped highlightOnHover withRowBorders={false} fz="sm">
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Date</Table.Th>
                  <Table.Th>Description</Table.Th>
                  <Table.Th>Amount</Table.Th>
                  <Table.Th>Status</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {DEMO_PAYMENTS.map((p, i) => (
                  <Table.Tr key={i}>
                    <Table.Td c="dimmed" style={{ whiteSpace: "nowrap" }}>{p.date}</Table.Td>
                    <Table.Td>{p.description}</Table.Td>
                    <Table.Td style={{ whiteSpace: "nowrap" }}>{p.amount.toFixed(2)}€</Table.Td>
                    <Table.Td>
                      <Badge
                        size="sm"
                        color={PAYMENT_CONFIG[p.status].color}
                        variant="light"
                        leftSection={PAYMENT_CONFIG[p.status].icon}
                      >
                        {p.status}
                      </Badge>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
        </div>

      </Stack>
    </>
  );
}
